import { useEffect } from "react";
import { useLocation } from "react-router-dom";

let navigationStackIndex = 0;

export const useNavigationPrompt = (
  isDirty: boolean,
  onCustomPrompt: (action: string) => void
) => {
  const location = useLocation();

  // Initialize custom index on mount
  useEffect(() => {
    if (window.history.state?.__customIndex == null) {
      navigationStackIndex = 0;
      window.history.replaceState({ __customIndex: 0 }, "");
    } else {
      navigationStackIndex = window.history.state.__customIndex;
    }
  }, []);

  // Intercept pushState/replaceState (in-app)
  useEffect(() => {
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    const intercept = (
      fn: typeof originalPushState,
      type: "pushState" | "replaceState"
    ) => {
      return function (data: any, unused: string, url?: string | URL | null) {
        const nextPath = typeof url === "string" ? url : url?.toString();

        if (isDirty && nextPath && nextPath !== window.location.pathname) {
          onCustomPrompt(nextPath);
          return;
        }

        // Only increment index for pushState
        if (type === "pushState") {
          navigationStackIndex++;
        }

        return fn.call(
          window.history,
          { __customIndex: navigationStackIndex },
          unused,
          url
        );
      };
    };

    window.history.pushState = intercept(originalPushState, "pushState");
    window.history.replaceState = intercept(
      originalReplaceState,
      "replaceState"
    );

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [isDirty, onCustomPrompt, location.pathname]);

  // Handle browser back/forward (mouse or keyboard)
  // useEffect(() => {
  //   const handlePopState = (e: PopStateEvent) => {
  //     const newIndex = e.state?.__customIndex;

  //     if (!isDirty || newIndex == null) return;

  //     const currentPath = window.location.pathname + window.location.search;

  //     // Restore current page to prevent browser nav
  //     window.history.pushState(
  //       { __customIndex: navigationStackIndex },
  //       "",
  //       currentPath
  //     );

  //     const direction = newIndex < navigationStackIndex ? "back" : "forward";
  //     onCustomPrompt(direction);
  //   };

  //   window.addEventListener("popstate", handlePopState);
  //   return () => window.removeEventListener("popstate", handlePopState);
  // }, [isDirty, onCustomPrompt]);

  // Handle keyboard reload
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (!isDirty) return;

      const key = e.key.toLowerCase();
      if (
        key === "f5" ||
        (e.ctrlKey && key === "r") ||
        (e.metaKey && key === "r")
      ) {
        e.preventDefault();
        onCustomPrompt("reload");
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isDirty, onCustomPrompt]);

  // Native confirm only for tab close / address bar
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        localStorage.setItem("reloadIntent", "true");
        e.preventDefault();
        e.returnValue = ""; // native dialog
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);
};
